import { FIRST_STEP_KEYS, type FirstStepKey, getItem, getTown, type TownId, venuesIn } from '@wyn/content';
import { balance } from './config/balance.ts';
import { assertGame } from './errors.ts';
import { type ActionResult, type GameEvent, type LedgerDelta, type PlayerState, SYSTEM } from './player.ts';

/**
 * The first-steps chain: GOAL.md's ten-minute journey, one step at a time. A new character starts on it
 * (`newFirstSteps`); older players and residents have no field and never see it. Steps may be done in any
 * order. Each pays a little Renown, and a couple pay a good that helps with what comes next, never coin:
 * `realm:inflation` has no room for another faucet.
 */
export interface FirstStepsState {
  /** When each step was done (epoch ms). */
  done: Partial<Record<FirstStepKey, number>>;
  /** When the player put the chain aside: nothing more is counted or paid. */
  dismissed?: number;
}

export interface FirstStepReward {
  renown: number;
  item?: { item: string; quantity: number };
}

export interface FirstStep {
  key: FirstStepKey;
  name: string;
  /** Where and how, in the player's own home town. */
  hint: (homeTown: TownId) => string;
  reward: FirstStepReward;
  /** Whether an event of the player's own action does this step. */
  test: (e: GameEvent, homeTown: TownId) => boolean;
}

const BANK = getTown(balance.bank.town).name;

export const FIRST_STEPS: readonly FirstStep[] = [
  {
    key: 'train',
    name: 'Train',
    hint: () => 'Spend some Vigour at the Training Yard.',
    reward: { renown: 10, item: { item: 'tankard-of-ale', quantity: 1 } },
    test: (e) => e.type === 'trained',
  },
  {
    key: 'venue',
    name: 'Play a game',
    hint: (t) => `Finish a game at one of ${getTown(t).name}’s venues.`,
    reward: { renown: 10 },
    test: (e, t) => e.type === 'minigame' && !!e.data?.engine && venuesIn(t).some((v) => v.key === e.data!.venue),
  },
  {
    key: 'shop',
    name: 'Go shopping',
    hint: () => 'Buy something at a shop in town.',
    reward: { renown: 10 },
    test: (e) => e.type === 'bought',
  },
  {
    key: 'road',
    name: 'Take the road',
    hint: () => 'Set out for another town.',
    reward: { renown: 10 },
    test: (e) => e.type === 'departed',
  },
  {
    key: 'trade',
    name: 'Sell or trade',
    hint: () => 'Put something up for sale at the Market, or trade with another player.',
    reward: { renown: 10 },
    test: (e) => (e.type === 'order-placed' && e.data?.side === 'sell') || e.type === 'trade' || e.type === 'market-sold' || e.type === 'market-bought',
  },
  {
    key: 'fight',
    name: 'Pick a fight',
    hint: () => 'Attack someone in town. The residents are always game.',
    reward: { renown: 10, item: { item: 'healing-draught', quantity: 1 } },
    test: (e) => e.type === 'fight' && e.data?.role === 'attacker',
  },
  {
    key: 'bank',
    name: 'Bank your coin',
    hint: () => `Deposit coin at the Royal Bank in ${BANK}.`,
    reward: { renown: 10 },
    test: (e) => e.type === 'deposit',
  },
];

/** Paid once every step is done, on top of the last step's own reward. */
export const FIRST_STEPS_DONE_REWARD: FirstStepReward = { renown: 30 };

export const newFirstSteps = (): FirstStepsState => ({ done: {} });

/** Every step done. */
export const firstStepsFinished = (s: FirstStepsState): boolean => FIRST_STEP_KEYS.every((k) => s.done[k] !== undefined);

/** Whether the chain is still being followed: begun, not finished and not put aside. */
export const onFirstSteps = (p: Pick<PlayerState, 'firstSteps'>): boolean => !!p.firstSteps && !p.firstSteps.dismissed && !firstStepsFinished(p.firstSteps);

/** What a step pays, in words: "+10 Renown · 1 × tankard of ale". */
export function firstStepRewardText(r: FirstStepReward): string {
  return [`+${r.renown} Renown`, ...(r.item ? [`${r.item.quantity} × ${getItem(r.item.item).name.toLowerCase()}`] : [])].join(' · ');
}

/**
 * Mark every step the action's events do, and pay for each: Renown on the row, goods ledgered as
 * `first-steps:<key>` against SYSTEM at common quality, and a `first-step` event. Doing the last step
 * also pays `FIRST_STEPS_DONE_REWARD`. A player not on the chain is returned unchanged. Called by
 * `applyProgress`, so it runs inside the action's own transaction.
 */
export function advanceFirstSteps(player: PlayerState, events: readonly GameEvent[], now: number): { player: PlayerState; events: GameEvent[]; ledger: LedgerDelta[] } {
  if (!onFirstSteps(player)) return { player, events: [], ledger: [] };
  const done = { ...player.firstSteps!.done };
  let renown = 0;
  const out: GameEvent[] = [];
  const ledger: LedgerDelta[] = [];
  const pay = (r: FirstStepReward, reason: string) => {
    renown += r.renown;
    if (r.item) ledger.push({ kind: 'item', itemKey: r.item.item, quality: balance.start.quality, delta: r.item.quantity, reason, counterparty: SYSTEM });
  };
  for (const step of FIRST_STEPS) {
    if (done[step.key] !== undefined || !events.some((e) => step.test(e, player.homeTown))) continue;
    done[step.key] = now;
    pay(step.reward, `first-steps:${step.key}`);
    out.push({ type: 'first-step', text: `First steps: ${step.name}. ${firstStepRewardText(step.reward)}.`, data: { step: step.key } });
  }
  if (out.length === 0) return { player, events: [], ledger: [] };
  const firstSteps: FirstStepsState = { ...player.firstSteps!, done };
  if (firstStepsFinished(firstSteps)) {
    pay(FIRST_STEPS_DONE_REWARD, 'first-steps:done');
    out.push({ type: 'first-steps-done', text: `You have found your feet in the realm. ${firstStepRewardText(FIRST_STEPS_DONE_REWARD)}.` });
  }
  return { player: { ...player, renown: player.renown + renown, firstSteps }, events: out, ledger };
}

/** Put the chain aside. The panel goes, and nothing more is counted or paid. */
export function dismissFirstSteps(p: PlayerState, now: number): ActionResult<{ dismissed: true }> {
  assertGame(onFirstSteps(p), 'INVALID', 'You have no first steps left to put aside.');
  return { player: { ...p, firstSteps: { ...p.firstSteps!, dismissed: now } }, ledger: [], events: [], outcome: { dismissed: true } };
}
